import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import LoadThemeComponent from '../../../../base/LoadThemeComponent';
import trackQuickPickButtonClicked from '../../../../base/trackers/helpers';

import { LotteryRanges, PickedNumber, SelectSignalPick, SetStateFunction } from '../../Types/Types';

import { isPickValidLine, PRIMARY_NUMBERS, RANGE_TO } from '../../helpers/linesHelper';
import useLinesHelper from '../../helpers/useLinesHelper';

import BallList from './BallList';

import { arrayWith, generateRandomLine, numberArray } from 'utils/helpers';
import { isDefault, isPremierDubai } from 'utils/template';

type Props = {
    readonly ranges: LotteryRanges;
    readonly numbersToPick: number;
    readonly maxLinesPerTicket: number;
    readonly pickedNumbers: Array<PickedNumber>;
    readonly setPickedNumbers: SetStateFunction<Array<PickedNumber>>;
    readonly selectSignal: SelectSignalPick;
};

function Picker({
    ranges,
    numbersToPick,
    pickedNumbers,
    setPickedNumbers,
    maxLinesPerTicket = 10,
    selectSignal,
}: Props) {
    const [lineToPick, setLineToPick] = useState(null);
    // TODO: move it higher and pass it also to selected lines component

    const { t } = useTranslation();
    const { lottery, lines, setLines } = useLinesHelper();

    function generateLine() {
        return [...Array(numbersToPick)].map((n, i) => {
            const range = ranges[i];
            const ball = pickedNumbers.find((pickedNumber: PickedNumber) => pickedNumber.pool === i);

            return (
                <BallList
                    key={`${i + 1}`}
                    pool={i}
                    length={range[1] - range[0] + 1}
                    number={typeof ball === 'undefined' ? null : ball.value}
                    updateValueInNewLine={updateValueInNewLine}
                />
            );
        });
    }

    const [showModal, setShow] = useState<boolean>();
    const [isDone, setIsDone] = useState(false);
    useEffect(() => setShow(selectSignal?.clickSource !== 'homepage'), [selectSignal]);

    useEffect(() => {
        if (isDone) {
            setIsDone(false);
        }
    }, [isDone]);

    useEffect(() => {
        if (pickedNumbers.length === numbersToPick) {
            if (isDefault() && !isDone) return;

            const values = [...pickedNumbers].sort((a, b) => a.pool - b.pool).map((number) => [number.value]);

            // Filter out empty lines
            const validLines = lines.filter((line) => isPickValidLine(line, numbersToPick));

            if (validLines.length >= maxLinesPerTicket) {
                const updatedLines = arrayWith(validLines, validLines.length - 1, values);
                setLines(updatedLines);
            } else {
                setLines([...validLines, [...values]]);
                setPickedNumbers([]);
            }
        }

        setLineToPick(generateLine());
    }, [pickedNumbers, isDone]);

    function updateValueInNewLine(pool, value) {
        const index = pickedNumbers.findIndex((ball: PickedNumber) => ball.pool === pool);

        if (index >= 0) {
            // update
            const clone = [...pickedNumbers];
            clone[index] = { pool, value };
            setPickedNumbers(clone);
        } else {
            // insert
            setPickedNumbers([...pickedNumbers, { pool, value }]);
        }
    }

    function onClickQuickPick() {
        const randomLine = generateRandomLine(ranges, numbersToPick);
        const quickPickedNumbers: Array<PickedNumber> = [];

        randomLine.forEach((value, index) => {
            quickPickedNumbers.push({
                value: value[0], // will work for pick only
                pool: index,
            });
        });

        setPickedNumbers(quickPickedNumbers);
    }

    function setQuickPickNumbersDefault() {
        const randomLine = generateRandomLine(ranges, numbersToPick);
        const quickPickedNumbers: Array<PickedNumber> = [];

        if (selectSignal?.clickSource === 'uncheckedView') {
            randomLine.forEach((value, index) => {
                quickPickedNumbers.push({
                    value: value[0],
                    pool: index,
                });
            });
            setPickedNumbers(quickPickedNumbers);
        } else {
            const newLines = arrayWith(
                lines,
                selectSignal.lineIndex,
                lines[selectSignal.lineIndex].map((_, columnIndex) => [randomLine[columnIndex][0]])
            );

            setLines(newLines);
        }
    }

    function onClickClear() {
        setPickedNumbers([]);
    }

    function handleClickClearDefault() {
        if (selectSignal?.clickSource === 'uncheckedView') {
            setPickedNumbers([]);
        } else {
            const newLines = lines.filter((_, lineIndex) => lineIndex !== selectSignal.lineIndex);
            setLines(newLines);
        }
    }

    const baseView = () => (
        <div>
            <div className="justify-content-center purchase-line-container rounded">
                <div className="play-picker">
                    <div className="row row-buttons no-gutters d-flex justify-content-center mb-5">
                        <div>
                            <button
                                className="btn btn-primary quick-pick mx-2"
                                type="button"
                                onClick={() => {
                                    onClickQuickPick();
                                    trackQuickPickButtonClicked(lottery.slug);
                                }}
                            >
                                {isPremierDubai() ? t('playPicker.autoChoose') : t('playPicker.quickPick')}
                            </button>
                        </div>
                        <div>
                            <button
                                className="btn btn-secondary purchase-line-clear mx-2"
                                type="button"
                                onClick={() => onClickClear()}
                            >
                                {t('playPicker.clear')}
                            </button>
                        </div>
                    </div>
                    <div className="purchase-line">{lineToPick}</div>
                </div>
            </div>
        </div>
    );

    function updateValueInSelectedLine(columnIndex: number, newValue: number) {
        const selectedLineIndex = selectSignal?.lineIndex;

        if (selectedLineIndex >= 0) {
            const newLine = arrayWith(lines[selectedLineIndex], columnIndex, [newValue]);
            setLines(arrayWith(lines, selectedLineIndex, newLine));
        }
    }

    function generateDefaultLines() {
        return numberArray(numbersToPick).map((rowIndex) => {
            return (
                <div
                    key={rowIndex}
                    className="row-numbers g-0 purchase-line-numbers pick justify-content-center play-balls-row"
                >
                    {numberArray(ranges[PRIMARY_NUMBERS][RANGE_TO], true).map((columnIndex) => {
                        const isSelected =
                            lines?.[selectSignal?.lineIndex]?.[rowIndex]?.[PRIMARY_NUMBERS] === columnIndex;
                        const ball = pickedNumbers?.find(
                            (pickedNumber: PickedNumber) => pickedNumber.pool === rowIndex
                        );

                        const isSelectedUnchecked = ball?.value === columnIndex;

                        const shouldMarkAsPicked =
                            selectSignal?.clickSource === 'checkedView' ? isSelected : isSelectedUnchecked;

                        const handleBallClick =
                            selectSignal?.clickSource === 'uncheckedView'
                                ? () => updateValueInNewLine(rowIndex, columnIndex)
                                : () => updateValueInSelectedLine(rowIndex, columnIndex);

                        return (
                            <div
                                key={columnIndex}
                                className={`number text-center purchase-line-number purchase-line-to-pick d-flex align-items-center justify-content-center ${
                                    shouldMarkAsPicked ? 'picked' : ''
                                }`}
                                onClick={handleBallClick}
                            >
                                {columnIndex}
                                <i className="fas fa-times" />
                            </div>
                        );
                    })}
                </div>
            );
        });
    }

    const defaultView = () => (
        <Modal className="play-picker-modal pick" show={showModal} onHide={() => setShow(false)} centered>
            <Modal.Header closeButton />
            <Modal.Body>
                <div className="play-picker">
                    <div className="row-buttons g-0 d-flex justify-content-center">
                        <div>
                            <button
                                type="button"
                                onClick={() => {
                                    setQuickPickNumbersDefault();
                                    trackQuickPickButtonClicked(lottery.slug);
                                }}
                                className="btn btn-secondary quick-pick"
                            >
                                {t('playPicker.quickPick')}
                            </button>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={() => handleClickClearDefault()}
                                className="btn btn-secondary purchase-line-clear"
                            >
                                {t('playPicker.clear')}
                            </button>
                        </div>
                    </div>
                    <div className="pick-info d-flex justify-content-center">
                        {numbersToPick === pickedNumbers.length && selectSignal?.clickSource === 'uncheckedView' && (
                            <i className="fas fa-check-circle" />
                        )}
                        <span className="d-block d-sm-inline"> {t('playPicker.pickNNumbers', { numbersToPick })}</span>
                        <span className="d-block d-sm-inline"> {t('playPicker.oneFromEachLine')}</span>
                    </div>
                    <div className="purchase-line">
                        <div className="row-numbers g-0 purchase-line-numbers">{generateDefaultLines()}</div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-primary mx-auto"
                    onClick={() => {
                        setIsDone(true);
                        setShow(false);
                    }}
                >
                    {t('playPicker.modal.done')}
                </button>
            </Modal.Footer>
        </Modal>
    );

    return <LoadThemeComponent defaultView={defaultView} baseView={baseView} />;
}

export default Picker;
